import { useState, useEffect, useMemo, type CSSProperties } from "react";
import SectionErrorBoundary from "@/components/SectionErrorBoundary";
import HomePageSkeleton from "@/components/skeletons/HomePageSkeleton";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { getProductImageUrl } from "@/hooks/useCatalog";
import BottomNav from "@/components/BottomNav";
import InstallGuideModal, { isIosSafari, isStandalone } from "@/components/InstallGuideModal";
import AiRestoredModal from "@/components/AiRestoredModal";
import { Bell, BellRing, BellOff, Globe, Camera, Search, BookOpen, Clock, SlidersHorizontal, FlaskConical } from "lucide-react";
import DesktopSidebar from "@/components/DesktopSidebar";
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from "@/i18n";
import PastDueBanner from "@/components/PastDueBanner";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useAdminThreads } from "@/hooks/useFeedback";
import { useSessionTracker } from "@/hooks/useSession";
import { buildRecipeRoute } from "@/lib/recipeRoute";
import { useTranslation } from "react-i18next";
import { useLocale } from "@/contexts/LocaleContext";
import { getLocalizedRecipesByIds } from "@/lib/localizedRecipes";
import { resolveRecipeImageFull, hasRealImage } from "@/lib/resolveRecipeImage";
import { getCorePriority } from "@/lib/contentPriority";
import { getCollectionPath } from "@/lib/collectionSlugs";
import { useLocalizedRecipes } from "@/hooks/useLocalizedRecipes";
import { useScreenTime } from "@/hooks/useScreenTime";
import { buildRecipeCountsByModule, countOwnedRecipeModules } from "@/lib/ownedContentCounts";
import s from "./Inicio.module.css";

const formatTime = (time?: string | null) => {
  if (!time) return null;
  return time.replace(/\s*minutos?/i, " min").replace(/\s*minutes?/i, " min");
};

const GLUCOPHOTO_IDS = ["7330458", "7468894", "7487694"];

const MOBILE_BREAKPOINT = 768;

type OwnedContentItem = {
  id: string;
  label: string;
  description: string | null;
  imageUrl: string | null;
  imageUrlMobile: string | null;
  path: string;
  recipeCount: number;
};

function getTimeGreeting(): { key: string } {
  const hour = new Date().getHours();
  if (hour < 12) return { key: "home.good_morning" };
  if (hour < 17) return { key: "home.good_afternoon" };
  return { key: "home.good_evening" };
}

// ─────────────────────────────────────────────────────────────────────────────

const Inicio = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, hasProduct, testerMode, setTesterMode, setTesterProducts, setTesterSubscriptionStatus, hasActiveSubscription, refreshProfile } = useAuth();
  useScreenTime("home");
  // Home renders its own page shell (sidebar + main), not AppLayout — AppLayout's
  // shared <main> applies md:max-w-4xl + md:mx-auto on top of its own sidebar-flex
  // row, which recenters content instead of filling it. Every other screen keeps
  // using AppLayout unchanged.
  useSessionTracker();
  const hasMundpaySub =
    hasProduct("7381203") || hasProduct("7381661") || hasProduct("7456362") || hasActiveSubscription();
  const [refreshingAccess, setRefreshingAccess] = useState(false);
  const [showInstallGuide, setShowInstallGuide] = useState(false);
  const [showAiRestored, setShowAiRestored] = useState(false);
  const [mobileOfferModal, setMobileOfferModal] = useState<OwnedContentItem | null>(null);
  const [mobileOfferModalClosing, setMobileOfferModalClosing] = useState(false);
  const isAdmin = user?.isAdmin ?? false;
  const { language, setLanguage } = useLocale();
  const { pushState, isSubscribed, subscribe: pushSubscribe, unsubscribe: pushUnsubscribe, loading: pushLoading } = usePushNotifications();
  const { pendingCount: adminFeedbackPending } = useAdminThreads();
  const displayName = user?.display_name || user?.email?.split("@")[0] || "";
  const greeting = getTimeGreeting();

  // ── Auto-show install guide for iOS Safari users without the PWA ──────────
  useEffect(() => {
    if (isIosSafari() && !isStandalone()) {
      if (localStorage.getItem("install_guide_shown") === "true") return;
      const timer = setTimeout(() => {
        setShowInstallGuide(true);
        localStorage.setItem("install_guide_shown", "true");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, []);

  // ── One-time "AI features restored" notice ─────────────────────────────────
  useEffect(() => {
    if (!user?.id) return;
    const resolvedKey = `onboarding_fully_resolved_v1_${user.id}`;

    const maybeShow = () => {
      if (localStorage.getItem(resolvedKey) !== "true") return false;
      if (localStorage.getItem("ai_restored_v1_shown") !== "true") {
        setShowAiRestored(true);
      }
      return true;
    };

    if (maybeShow()) return;
    const interval = setInterval(() => {
      if (maybeShow()) clearInterval(interval);
    }, 1000);
    return () => clearInterval(interval);
  }, [user?.id]);

  const handleAiRestoredOpenChange = (open: boolean) => {
    setShowAiRestored(open);
    if (!open) {
      localStorage.setItem("ai_restored_v1_shown", "true");
    }
  };

  // ── Data: recipe modules ───────────────────────────────────────────────────
  const { data: recipeOfertas } = useQuery({
    queryKey: ["recipe-modules-home", language],
    queryFn: async () => {
      const { data } = await supabase
        .from("recipe_modules")
        .select("*")
        .eq("active", true)
        .order("sort_order");
      return data || [];
    },
    staleTime: 1000 * 60 * 60,
  });

  // ── Data: recipe counts per module (for "Your content" cards) ──────────────
  // A dedicated, paginated count query — rawAllRecipes below is capped at
  // Supabase/PostgREST's default 1000-row limit (no .range()), which silently
  // undercounts modules once total recipes exceed that, so it can't be reused
  // for counts. This fetches only module_id in batches of 1000 until exhausted.
  const { data: recipeCounts } = useQuery({
    queryKey: ["recipe-counts-by-module"],
    queryFn: async () => {
      const counts: Record<string, number> = {};
      const batchSize = 1000;
      for (let from = 0; ; from += batchSize) {
        const { data, error } = await supabase
          .from("recipes")
          .select("module_id")
          .not("module_id", "is", null)
          .order("id")
          .range(from, from + batchSize - 1);
        if (error) throw error;
        if (!data?.length) break;
        for (const r of data) {
          if (r.module_id) counts[r.module_id] = (counts[r.module_id] || 0) + 1;
        }
        if (data.length < batchSize) break;
      }
      return counts;
    },
    staleTime: 1000 * 60 * 10,
  });

  // ── Data: all recipes ──────────────────────────────────────────────────────
  const rawAllRecipes = useLocalizedRecipes(
    useQuery({
      queryKey: ["all-recipes-home"],
      queryFn: async () => {
        const { data } = await supabase.from("recipes")
          .select("id,title,image,image_group,category,total_time,time,prep_time,semaforo,module_id,language,sort_order,tags,meal_moment,difficulty,demand_score")
          .order("demand_score", { ascending: false })
          .order("sort_order");
        return data || [];
      },
      staleTime: 30 * 60 * 1000,
      gcTime: 60 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: true,
    }).data,
  );

  // ── Data: recently viewed ──────────────────────────────────────────────────
  const { data: recentlyViewed } = useQuery({
    queryKey: ["recently-viewed-recipes", user?.id, language],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data: history } = await supabase
        .from("recipe_view_history")
        .select("recipe_id, last_viewed_at")
        .eq("user_id", user.id)
        .order("last_viewed_at", { ascending: false })
        .limit(10);
      if (!history || history.length === 0) return [];
      const ids = history.map((h) => h.recipe_id);
      return await getLocalizedRecipesByIds(ids, language);
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 10,
    gcTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // ── Derived: recipes with images ──────────────────────────────────────────
  const allWithImages = useMemo(() => (rawAllRecipes || []).filter(hasRealImage), [rawAllRecipes]);

  const recipeCountsByModule = useMemo(
    () => buildRecipeCountsByModule(rawAllRecipes || []),
    [rawAllRecipes],
  );

  const ownedCoreModuleCount = useMemo(
    () =>
      countOwnedRecipeModules({
        recipeModules: recipeOfertas || [],
        recipeCountsByModule,
        hasProduct,
        excludedProductIds: GLUCOPHOTO_IDS,
      }),
    [recipeOfertas, recipeCountsByModule, hasProduct],
  );

  // ── Recommended recipes ────────────────────────────────────────────────────
  const recommendedRecipes = useMemo(() => {
    const viewedIds = new Set((recentlyViewed || []).map((r: any) => r.id));
    const candidates = allWithImages
      .filter((r: any) => !viewedIds.has(r.id))
      .filter((r: any) => !r.language || r.language === language);
    return [...candidates].sort((a: any, b: any) => (b.demand_score ?? 50) - (a.demand_score ?? 50)).slice(0, 8);
  }, [allWithImages, recentlyViewed, language]);

  // ── Owned content ("Your content") ─────────────────────────────────────────
  const ownedContentItems = useMemo(() => {
    if (!recipeOfertas) return [];
    const items: OwnedContentItem[] = [];

    const resolveImg = (img: string | null | undefined) =>
      img ? (img.startsWith("http") ? img : getProductImageUrl(img)) : null;

    // Each hotmart_product_id is its own independent offer — Panadería Cero,
    // Clean Bakes and Brotglück are 3 different products, never grouped
    // together. Within a single product, its ES/EN/DE recipe_modules rows
    // are language variants of the SAME offer, so only one is shown, picked
    // by the user's current language (falling back to ES, then EN).
    const ownedModules = (recipeOfertas || [])
      .filter((m: any) => m.hotmart_product_id && hasProduct(m.hotmart_product_id))
      .filter((m: any) => !GLUCOPHOTO_IDS.includes(m.hotmart_product_id));

    const modulesByProduct = new Map<string, any[]>();
    for (const mod of ownedModules) {
      const list = modulesByProduct.get(mod.hotmart_product_id) || [];
      list.push(mod);
      modulesByProduct.set(mod.hotmart_product_id, list);
    }

    const pickVariant = (mods: any[]) =>
      mods.find((m) => m.language === language) ||
      mods.find((m) => m.language === "es") ||
      mods.find((m) => m.language === "en") ||
      mods[0];

    const selectedModules = Array.from(modulesByProduct.values())
      .map(pickVariant)
      .sort((a: any, b: any) => getCorePriority(a.hotmart_product_id) - getCorePriority(b.hotmart_product_id));

    for (const mod of selectedModules) {
      items.push({
        id: mod.id,
        label: mod.name,
        description: mod.description || null,
        imageUrl: resolveImg(mod.image),
        imageUrlMobile: resolveImg(mod.image_mobile) || resolveImg(mod.image),
        path: getCollectionPath(mod.id, mod.name),
        recipeCount: recipeCounts?.[mod.id] || 0,
      });
    }

    // La Botica de la Abuela isn't a recipe_modules row — added manually.
    // (Clean Remedies IS a recipe_modules row, already included above.)
    if (hasProduct("8131891")) {
      items.push({
        id: "la-botica",
        label: "La Botica de la Abuela",
        description: "Remedios naturales ancestrales para el bienestar general",
        imageUrl: null,
        imageUrlMobile: null,
        path: "/la-botica",
        recipeCount: 0,
      });
    }

    return items;
  }, [recipeOfertas, hasProduct, language, recipeCounts]);

  const isHomeLoading = !rawAllRecipes || !recipeOfertas;

  // Mobile shows an offer detail modal instead of navigating straight away;
  // desktop keeps the direct navigation it already had.
  const handleContentClick = (item: OwnedContentItem) => {
    if (window.innerWidth <= MOBILE_BREAKPOINT) {
      setMobileOfferModal(item);
    } else {
      navigate(item.path);
    }
  };

  // Plays the reverse (slide-down/fade-out) animation before unmounting —
  // matches the CSS closing keyframes' duration (250ms) in Inicio.module.css.
  const closeOfferModal = (afterClose?: () => void) => {
    setMobileOfferModalClosing(true);
    setTimeout(() => {
      setMobileOfferModal(null);
      setMobileOfferModalClosing(false);
      afterClose?.();
    }, 250);
  };

  const cycleLanguage = () => {
    const idx = SUPPORTED_LANGUAGES.indexOf(language as SupportedLanguage);
    const next = SUPPORTED_LANGUAGES[(idx + 1) % SUPPORTED_LANGUAGES.length];
    setLanguage(next);
  };

  const toggleNotifications = async () => {
    if (pushLoading) return;
    if (pushState === "denied") {
      toast({ title: t("notifications.blocked_title"), description: t("notifications.blocked_desc"), variant: "destructive" });
      return;
    }
    if (isSubscribed) {
      const ok = await pushUnsubscribe();
      if (ok) toast({ title: t("notifications.off") });
    } else {
      const ok = await pushSubscribe();
      if (ok) toast({ title: t("notifications.on") });
    }
  };

  const goSearch = () => navigate("/recipes", { state: { autoFocusSearch: true } });

  return (
    <>
      <div className={s.app}>
        <DesktopSidebar />

        <main className={s.main}>
          <div className={s.container}>
            {isHomeLoading ? (
              <HomePageSkeleton />
            ) : (
              <>
                {/* ── Top bar ── */}
                <div className={s.topbar}>
                  <div>
                    <h1 className={s.h1}>{t(greeting.key, { defaultValue: "Hello" })}, {displayName || user?.email?.split("@")[0] || ""} 👋</h1>
                    <span className={s.badge}>
                      {hasMundpaySub ? `✓ ${t("home.sub_active_banner")}` : t("home.no_sub_banner")}
                    </span>
                    <div className={s.subtitle}>{t("home.subtitle_cook_today", "What would you like to cook today?")}</div>
                  </div>

                  <div className={s.topActions}>
                    <button onClick={toggleNotifications} disabled={pushLoading} className={s.roundBtn} aria-label="Notifications">
                      {pushState === "denied" ? <BellOff size={18} /> : isSubscribed ? <BellRing size={18} /> : <Bell size={18} />}
                      {isAdmin && !testerMode && adminFeedbackPending > 0 && (
                        <span
                          className="absolute flex items-center justify-center font-bold text-white"
                          style={{ top: -3, right: -3, width: 16, height: 16, backgroundColor: "#D04020", borderRadius: "50%", fontSize: 9, border: "2px solid #fffaf2" }}
                        >
                          {adminFeedbackPending}
                        </span>
                      )}
                    </button>
                    <button onClick={cycleLanguage} className={s.roundBtn} title={language === "es" ? "Switch to English" : "Cambiar a Español"} aria-label="Language">
                      <Globe size={18} />
                    </button>
                  </div>
                </div>

                {/* ── Search ── */}
                <button onClick={goSearch} className={s.search}>
                  <span className="flex items-center gap-2">
                    <Search size={18} />{t("search.placeholder", "Search recipes, ingredients, or products...")}
                  </span>
                  <SlidersHorizontal size={16} />
                </button>

                {/* ── No-access refresh (only for users with 0 content) ── */}
                {ownedCoreModuleCount === 0 && (
                  <div className="mb-6 px-1">
                    <div className="px-4 py-3 rounded-2xl flex items-center justify-between gap-3 animate-fade-in bg-white border border-[#eadfcd]">
                      <div className="min-w-0">
                        <p className="text-sm font-bold" style={{ color: "#1f241f" }}>{t("home.no_access_title")}</p>
                        <p className="text-xs mt-0.5" style={{ color: "#756b5d" }}>{t("home.no_access_desc")}</p>
                      </div>
                      <button
                        onClick={async () => { setRefreshingAccess(true); await refreshProfile(); setRefreshingAccess(false); }}
                        disabled={refreshingAccess}
                        className="shrink-0 px-3 py-2 rounded-xl text-white text-xs font-bold disabled:opacity-50 active:scale-95 transition-all"
                        style={{ backgroundColor: "#5f8a59" }}
                      >
                        {refreshingAccess ? "..." : t("home.no_access_refresh")}
                      </button>
                    </div>
                  </div>
                )}

                <PastDueBanner />

                {/* ══════════════════════════════════════
                    Quick actions — Browse / Continue / Analyze
                    (the reference's 4th "Plan my meals" card is dropped —
                    Meal Plan doesn't appear anywhere on Home)
                ══════════════════════════════════════ */}
                <section className={s.actionsGrid}>
                  <button onClick={() => navigate("/recipes")} className={`${s.actionCard} ${s.actionCardGreen}`}>
                    <div>
                      <div className={s.icon}><BookOpen size={22} /></div>
                      <div className={s.actionTitle}>{t("home.action_browse_recipes", "Browse my recipes")}</div>
                      <div className={s.actionSub}>{t("home.action_browse_recipes_desc", "Explore all your purchased content")}</div>
                    </div>
                    <div>›</div>
                  </button>

                  <button
                    onClick={() => document.getElementById("continue-section")?.scrollIntoView({ behavior: "smooth", block: "start" })}
                    className={`${s.actionCard} ${s.actionCardGold}`}
                  >
                    <div>
                      <div className={s.icon}><Clock size={22} /></div>
                      <div className={s.actionTitle}>{t("home.action_continue_cooking", "Continue cooking")}</div>
                      <div className={s.actionSub}>{t("home.action_continue_cooking_desc", "Pick up where you left off")}</div>
                    </div>
                    <div>›</div>
                  </button>

                  <button onClick={() => navigate("/glucophoto")} className={`${s.actionCard} ${s.actionCardGreen}`}>
                    <div>
                      <div className={s.icon}><Camera size={22} /></div>
                      <div className={s.actionTitle}>{t("home.action_analyze_meal", "Analyze my meal")}</div>
                      <div className={s.actionSub}>{t("home.action_analyze_meal_desc", "Photo → glycemic estimate + tips")}</div>
                    </div>
                    <div>›</div>
                  </button>
                </section>

                {/* ══════════════════════════════════════
                    Your content
                ══════════════════════════════════════ */}
                {ownedContentItems.length > 0 && (
                  <SectionErrorBoundary section="my_content" blockType="recipe" compact>
                    <section className={s.section}>
                      <div className={s.sectionHead}>
                        <div>
                          <span className={s.sectionTitle}>{t("home.your_contents")}</span>
                          <span className={s.sectionDesc}>{t("home.your_contents_desc", "All the products you have access to")}</span>
                        </div>
                        {ownedContentItems.length > 4 && (
                          <button onClick={() => navigate("/my-content")} className={s.view}>{t("recipes.view_all", "View all")} →</button>
                        )}
                      </div>

                      <div className={s.contentGrid}>
                        {ownedContentItems.map((item) => (
                          <button key={item.id} onClick={() => handleContentClick(item)} className={s.productCard}>
                            <div
                              className={s.productImg}
                              style={{
                                ...(item.imageUrl ? { "--product-img-desktop": `url('${item.imageUrl}')` } : {}),
                                ...(item.imageUrlMobile ? { "--product-img-mobile": `url('${item.imageUrlMobile}')` } : {}),
                              } as CSSProperties}
                            />
                            <div className={s.productBody}>
                              <div className={s.productTitle}>{item.label}</div>
                              {item.description && <div className={s.productSub}>{item.description}</div>}
                              {item.recipeCount > 0 && <span className={s.pill}>{item.recipeCount} {t("home.recipes_count_label", "recipes")}</span>}
                            </div>
                          </button>
                        ))}
                      </div>
                    </section>
                  </SectionErrorBoundary>
                )}

                {/* ══════════════════════════════════════
                    Continue where you left off
                    (no progress bar — no real per-recipe progress data exists)
                ══════════════════════════════════════ */}
                {recentlyViewed && recentlyViewed.length > 0 && (
                  <SectionErrorBoundary section="continue_watching" blockType="recipe" compact>
                    <section className={s.section} id="continue-section">
                      <div className={s.sectionHead}>
                        <span className={s.sectionTitle}>{t("home.continue_where_left")}</span>
                        <button onClick={() => navigate("/my-content")} className={s.view}>{t("recipes.view_all", "View all")} →</button>
                      </div>

                      <div className={s.recipeRow}>
                        {[...recentlyViewed]
                          .sort((a: any, b: any) => (hasRealImage(b) ? 1 : 0) - (hasRealImage(a) ? 1 : 0))
                          .slice(0, 5)
                          .map((recipe: any) => {
                            const img = resolveRecipeImageFull(recipe.image, recipe.image_group, recipe.category).url;
                            const time = formatTime(recipe.total_time || recipe.time || recipe.prep_time);
                            return (
                              <button key={recipe.id} onClick={() => navigate(buildRecipeRoute(recipe))} className={s.recipeCard}>
                                <div className={s.recipeImg} style={img ? { backgroundImage: `url('${img}')` } : undefined}>
                                  {time && <span className={s.time}>{time}</span>}
                                </div>
                                <div className={s.recipeBody}>
                                  <div className={s.recipeTitle}>{recipe.title}</div>
                                </div>
                              </button>
                            );
                          })}
                      </div>
                    </section>
                  </SectionErrorBoundary>
                )}

                {/* ══════════════════════════════════════
                    GlucoPhoto banner
                ══════════════════════════════════════ */}
                {GLUCOPHOTO_IDS.some((id) => hasProduct(id)) && (
                  <button onClick={() => navigate("/glucophoto")} className={s.glucoBanner}>
                    <div>
                      <strong className={s.glucoStrong}>📷 {t("home.glucophoto_cta", "Analyze your meal")}</strong>
                      <span className={s.glucoSpan}>{t("home.glucophoto_cta_desc", "Take a photo and get a glycemic estimate with practical tips.")}</span>
                    </div>
                    <div>›</div>
                  </button>
                )}

                {/* ══════════════════════════════════════
                    Recommended for you
                ══════════════════════════════════════ */}
                {recommendedRecipes.length > 0 && (
                  <SectionErrorBoundary section="recommended" blockType="recipe" compact>
                    <section className={s.section}>
                      <div className={s.sectionHead}>
                        <span className={s.sectionTitle}>{t("home.recommended_for_you")}</span>
                        <button onClick={() => navigate("/recipes")} className={s.view}>{t("recipes.view_all", "View all")} →</button>
                      </div>

                      <div className={s.recipeRow}>
                        {recommendedRecipes.slice(0, 5).map((recipe: any) => {
                          const img = resolveRecipeImageFull(recipe.image, recipe.image_group, recipe.category).url;
                          const time = formatTime(recipe.total_time || recipe.time || recipe.prep_time);
                          return (
                            <button key={recipe.id} onClick={() => navigate(buildRecipeRoute(recipe))} className={s.recipeCard}>
                              <div className={s.recipeImg} style={img ? { backgroundImage: `url('${img}')` } : undefined}>
                                {time && <span className={s.time}>{time}</span>}
                              </div>
                              <div className={s.recipeBody}>
                                <div className={s.recipeTitle}>{recipe.title}</div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </section>
                  </SectionErrorBoundary>
                )}

                {/* ── Admin shortcut ── */}
                {isAdmin && !testerMode && (
                  <div className="mb-4">
                    <button
                      onClick={() => navigate("/admin")}
                      className="w-full min-h-[52px] rounded-2xl text-base font-medium text-center active:scale-[0.97] transition-transform bg-white border"
                      style={{ borderColor: "#eadfcd", color: "#1f241f" }}
                    >
                      ⚙️ {t("nav.admin")}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </main>
      </div>

      {testerMode && (
        <button
          onClick={() => { setTesterMode(false); setTesterProducts([]); setTesterSubscriptionStatus(null); }}
          className="fixed top-3 right-3 z-50 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-white text-[11px] font-bold animate-pulse hover:animate-none active:scale-95 transition-transform"
          style={{ backgroundColor: "#B8966A", boxShadow: "0 4px 12px -2px #B8966A55" }}
        >
          <FlaskConical size={12} />
          {t("tester.title")}
        </button>
      )}

      {mobileOfferModal && (
        <div
          className={`${s.offerModalOverlay} ${mobileOfferModalClosing ? s.offerModalOverlayClosing : ""}`}
          onClick={() => closeOfferModal()}
        >
          <div
            className={`${s.offerModal} ${mobileOfferModalClosing ? s.offerModalClosing : ""}`}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => closeOfferModal()}
              className={s.offerModalClose}
              aria-label={t("common.close", "Close")}
            >
              ×
            </button>
            <div
              className={s.offerModalImg}
              style={mobileOfferModal.imageUrlMobile || mobileOfferModal.imageUrl ? {
                backgroundImage: `url('${mobileOfferModal.imageUrlMobile || mobileOfferModal.imageUrl}')`,
              } : undefined}
            />
            <div className={s.offerModalBody}>
              <div className={s.offerModalTitle}>{mobileOfferModal.label}</div>
              {mobileOfferModal.recipeCount > 0 && (
                <span className={s.pill}>{mobileOfferModal.recipeCount} {t("home.recipes_count_label", "recipes")}</span>
              )}
              {mobileOfferModal.description && (
                <div className={s.offerModalDesc}>{mobileOfferModal.description}</div>
              )}
              <button
                onClick={() => { const path = mobileOfferModal.path; closeOfferModal(() => navigate(path)); }}
                className={s.offerModalCta}
              >
                {t("home.access_recipes", "Access recipes")}
              </button>
            </div>
          </div>
        </div>
      )}

      <BottomNav />
      <InstallGuideModal open={showInstallGuide} onOpenChange={setShowInstallGuide} />
      <AiRestoredModal open={showAiRestored} onOpenChange={handleAiRestoredOpenChange} />
    </>
  );
};

export default Inicio;
